import TuneIcon from '@mui/icons-material/Tune';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { QueryClientContext, useQuery } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import type { SettingKey, UsedSettingDto } from '@app/shared';
import { api } from '../lib/api';
import { useFeatureReleased } from '../release';
import { ErrorState, LoadingState } from './PageState';

/**
 * The System Config settings each page acts on, as the server code reads them. The reference
 * shows a "Used system configurations" dialog on every page; what it lists there was not seen,
 * so these lists are 212Ez's own.
 */
const TAXES: SettingKey[] = ['TAX_ENABLED'];
const PAGES: Array<{ match: RegExp; keys: SettingKey[] }> = [
  {
    match: /^\/enrollments\/(new|[a-f\d]{24}\/step)/,
    keys: [
      'PREPAID_ENROLLMENT_PREFIX',
      'PP_ENROLLMENT_DISABLED_STATES',
      'DAILY_ENROLLMENT_LIMIT_ON',
      'DAILY_ENROLLMENT_MAX',
      'MONTHLY_ENROLLMENT_LIMIT_ON',
      'MONTHLY_ENROLLMENT_MAX',
      'EMAIL_MANDATORY',
      'PROHIBITED_EMAIL_DOMAINS',
      'SERVICE_ADDRESS_DUPLICATE_CHECK',
      'FRAUD_EMAIL_ENABLED',
      'FRAUD_EMAIL_MAX_ORDERS',
      'FRAUD_EMAIL_DAYS',
      'FRAUD_REVIEW_QUEUE',
      'REGULATORY_FEE_CENTS',
      'SKIP_ACTIVATION_FEE',
      ...TAXES,
      'TAX_ON_NEW_SIGNUP',
      'TAX_ON_EQUIPMENT',
    ],
  },
  { match: /^\/enrollments\/bulk/, keys: ['PREPAID_ENROLLMENT_PREFIX', 'EMAIL_MANDATORY'] },
  {
    match: /^\/customers(\/|$)/,
    keys: [
      'NOTES_CALLER_ID',
      'PROHIBITED_EMAIL_DOMAINS',
      'DISCONNECT_WITHOUT_MDN',
      'CARRIER_CHANGE_DEACTIVATE_OLD_MDN',
      'DATA_TOPUP_LIMIT_ON',
      'DATA_TOPUP_MAX_PER_PERIOD',
      'ALLOW_INSTANT_CUSTOM_PLANPURCHASE',
      'ALLOW_INSTANT_CUSTOM_PLAN_CHANGE',
      'KEEP_EXPIRY_DEFAULT_VALUE',
      'KEEP_EXPIRY_DEFAULT_VALUE_PLAN_CHANGE',
      'MIDCYCLE_PLAN_CHANGE_LIMIT_ON',
      'MIDCYCLE_PLAN_CHANGE_MAX',
      'ALLOW_LATE_FEE_AFTER_PLAN_EXPIRY',
      'LATE_FEE_AFTER_PLAN_EXPIRY',
      'ALLOW_RESTORE_FEE_FOR_SUSPENDED_ACCOUNT',
      'RESTORE_FEE_FOR_SUSPENDED_ACCOUNT',
      'PLAN_EXPIRY_ENABLED',
      'RENEW_AUTO_PAY',
      'RENEW_CUSTOMER_WALLET',
      ...TAXES,
      'TAX_ON_TOPUP',
      'TAX_ON_PLAN_RENEW',
      'TAX_ON_EQUIPMENT',
    ],
  },
  {
    match: /^\/settings\/users\/commission/,
    keys: [
      'COMMISSION_MATRIX_ENABLED',
      'MATRIX_COMMISSION_TYPE',
      'ALLOW_RECURRING_MATRIX_COMMISSION',
    ],
  },
  {
    match: /^\/settings\/users/,
    keys: [
      'LOGIN_PASSWORD_EXPIRY_DAYS',
      'AGENT_PASSWORD_EXPIRY_NO_LOGIN_DAYS',
      'PREVENT_MULTIPLE_LOGIN',
      'ALLOWED_MULTIPLE_LOGIN',
    ],
  },
];

/** The settings of a page address, or none. */
export const usedSettingKeys = (pathname: string): SettingKey[] =>
  PAGES.find((p) => p.match.test(pathname))?.keys ?? [];

function UsedConfigurationsButton() {
  const { pathname } = useLocation();
  const featureOn = useFeatureReleased();
  const keys = usedSettingKeys(pathname);
  const [open, setOpen] = useState(false);
  const used = useQuery({
    queryKey: ['system-configs', 'used', keys],
    queryFn: () =>
      api
        .get<{ data: UsedSettingDto[] }>('/system-configs/used', { keys: keys.join(',') })
        .then((r) => r.data),
    enabled: open,
  });
  if (keys.length === 0 || !featureOn('used-system-configurations')) return null;
  return (
    <>
      <Tooltip title="Used system configurations">
        <IconButton aria-label="Used system configurations" onClick={() => setOpen(true)}>
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Used system configurations</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {used.error ? (
            <ErrorState error={used.error} onRetry={() => void used.refetch()} />
          ) : !used.data ? (
            <LoadingState />
          ) : (
            <Table size="small" aria-label="Used system configurations">
              <TableHead>
                <TableRow>
                  <TableCell>Setting</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {used.data.map((s) => (
                  <TableRow key={s.key}>
                    <TableCell>
                      {s.label}
                      {s.code ? ` (${s.code})` : ''}
                    </TableCell>
                    <TableCell>{s.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Shown in a page header inside the app (it needs the router and the query client). */
export function UsedConfigurations() {
  const inRouter = useInRouterContext();
  const queries = useContext(QueryClientContext);
  return inRouter && queries ? <UsedConfigurationsButton /> : null;
}
